import React, { useMemo, useState } from 'react';
import { useCallCenter } from '../providers/CallCenterProvider';
import getTextColor from '../util/Color';
import {
    useGetInsuranceListQuery,
    useGetUnitListByTypeQuery,
    usePostReservationMutation,
} from '../api/rapidstor';
import Card from '../components/Card';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import { formatCurrency } from '../util/Currency';
import Button from '../components/Button';
import Modal from '../components/Modal';
import { isValidEmail } from '../util/validation';
import { useImmer } from 'use-immer';
import Deal from '@r6digital/rapidstor-types/types/Deal';

interface FormData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    agentName: string;
    agentComment: string;
}

export default function PricingCalculator() {
    /** Hooks */
    const {
        configuration,
        locations,
        selectedLocationCode,
        agentName,
        agentComments,
        setAgentDetails,
        setAgentName,
        setAgentComments,
    } = useCallCenter();
    const locationCode = selectedLocationCode;

    /** State */
    const [selectedLocation, setSelectedLocation] = useState<string>(locationCode || '');
    const [selectedUnitType, setSelectedUnitType] = useState<UnitType | null>(null);
    const [selectedDeal, setSelectedDeal] = useState<string>('');
    const [selectedInsurance, setSelectedInsurance] = useState<string>('');
    const [reservationModalOpen, setReservationModalOpen] = useState(false);
    const [reservationLoading, setReservationLoading] = useState(false);

    const INSURANCETAX = configuration?.pricingCalculator?.insuranceTaxRate ?? 1.2; // StoreProtect tax rate
    /** Queries */
    const [makeReservation] = usePostReservationMutation();
    const { data: unitTypes, isFetching: isLoadingUnitTypes } = useGetUnitListByTypeQuery(
        {
            corp: configuration?.appConfig.journey.corpCode ?? '',
            site: selectedLocation,
            locationCodes: [selectedLocation],
        },
        {
            skip: !selectedLocation || !configuration,
            refetchOnMountOrArgChange: true,
        }
    );

    const { data: insuranceList } = useGetInsuranceListQuery(
        {
            corp: configuration?.appConfig.journey.corpCode ?? '',
            site: selectedLocation,
        },
        {
            skip: !selectedLocation || !configuration,
            refetchOnMountOrArgChange: true,
        }
    );

    const [formData, setFormData] = useImmer<FormData>({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        agentName: agentName,
        agentComment: agentComments,
    });

    const sortedUnitTypes = useMemo(() => {
        if (!unitTypes || !unitTypes.unitTypes) {
            return [];
        }

        return [...unitTypes.unitTypes].sort((unitTypeA, unitTypeB) => {
            if (unitTypeA.area !== unitTypeB.area) {
                return unitTypeA.area - unitTypeB.area;
            }
            return unitTypeA.floor - unitTypeB.floor;
        });
    }, [unitTypes]);

    const dealsForUnitType = useMemo(() => {
        if (!selectedUnitType) return [];

        return selectedUnitType.deals.filter((d: Deal) => d.discountPlan);
    }, [selectedUnitType]);

    const selectedDealObject = useMemo(() => {
        return dealsForUnitType.find((deal) => deal._id === selectedDeal);
    }, [selectedDeal, dealsForUnitType]);

    const selectedInsuranceObject = useMemo(() => {
        return insuranceList?.results.find(
            (coverage) => coverage._id === selectedInsurance
        );
    }, [insuranceList, selectedInsurance]);

    const discountPerWeek = useMemo(() => {
        if (!selectedDealObject || !selectedUnitType) {
            return 0;
        }

        const currentPrice = selectedUnitType?.rates.rate || 0;
        const offerPrice = selectedDealObject?.rates.discountedRate || 0;

        return currentPrice - offerPrice;
    }, [selectedDealObject, selectedUnitType]);

    const percentageReduced = useMemo(() => {
        if (!selectedDealObject || !selectedUnitType) {
            return 0;
        }

        const currentPrice = selectedUnitType?.rates.rate || 0;
        const offerPrice = selectedDealObject?.rates.discountedRate || 0;

        return ((currentPrice - offerPrice) / currentPrice) * 100;
    }, [selectedDealObject, selectedUnitType]);

    const discountedWeeklyPrice = useMemo(() => {
        return selectedDealObject
            ? ((selectedUnitType?.rates.rate || 0) - discountPerWeek) / 2
            : 0;
    }, [discountPerWeek, selectedDealObject, selectedUnitType]);

    const weeklyOfferPrice = useMemo(() => {
        return discountedWeeklyPrice / 2;
    }, [discountedWeeklyPrice]);

    // Root already renders a loading/error/picker state before mounting this
    // route — this is only here to narrow the type for TypeScript, not a
    // real runtime path.
    if (!configuration) {
        return null;
    }

    const actionShowPrices = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedUnitType) {
            return false;
        }

        if (
            configuration.agentFieldType === 'email' &&
            !isValidEmail(formData.agentName)
        ) {
            alert('Agent Email must be a valid email address');
            return false;
        }

        setReservationLoading(true);

        try {
            setAgentName(formData.agentName);
            setAgentComments(formData.agentComment);
            setAgentDetails({
                agentName: formData.agentName,
                agentComments: formData.agentComment,
            });
            await makeReservation({
                locationCode: selectedLocation,
                corpCode: configuration.appConfig.journey.corpCode,
                payload: {
                    timezoneOffset:
                        (configuration.pricingCalculator?.timezoneOffsetMinutes ?? 0) *
                        -60,
                    neededDate: new Date().toISOString(),
                    tenant: {
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        email: formData.email,
                        phone: formData.phone,
                        tenantId: null,
                    },
                    customListing: true,
                    sourceUrl: window.location.href,
                    unitType: selectedUnitType._id,
                    deal: selectedDeal,
                    enquiry: true,
                    externalData: {
                        reservationSource: formData.agentName,
                        reservationComment: formData.agentComment,
                    },
                },
            });
            alert('Reservation successfully created');
            setReservationModalOpen(false);
        } catch (err) {
            console.error(err);
            alert('Error making reservation');
        }

        setReservationLoading(false);
    };

    return (
        <div className='grid grid-cols-1 md:grid-cols-2 p-8 gap-4'>
            <div className='flex flex-col gap-4'>
                <Card
                    loading={isLoadingUnitTypes}
                    className='flex items-center rounded border-4 border-solid'
                >
                    <span
                        className='block text-lg p-4'
                        style={{
                            backgroundColor: configuration.secondaryColor,
                            color: getTextColor(configuration.secondaryColor, '#fff'),
                        }}
                    >
                        Site:
                    </span>
                    <select
                        className='bg-white w-full p-4 border-none text-lg'
                        value={`${selectedLocation}`}
                        onChange={(e) => {
                            setSelectedLocation(e.target.value);
                        }}
                        style={{
                            height: 'initial',
                            marginTop: 'initial',
                            marginBottom: 'initial',
                        }}
                    >
                        <option value=''>Select a site</option>
                        {locations.map((site) => (
                            <option
                                key={site.code}
                                value={site.code}
                            >{`${site.code} - ${site.name}`}</option>
                        ))}
                    </select>
                </Card>

                <Card
                    loading={isLoadingUnitTypes}
                    className='flex items-center rounded border-4 border-solid'
                >
                    <span
                        className='block text-lg p-4'
                        style={{
                            backgroundColor: configuration.secondaryColor,
                            color: getTextColor(configuration.secondaryColor, '#fff'),
                        }}
                    >
                        Offer:
                    </span>
                    <select
                        className='bg-white w-full p-4 border-none text-lg'
                        value={selectedDeal}
                        onChange={(e) => {
                            setSelectedDeal(e.target.value);
                        }}
                        style={{
                            height: 'initial',
                            marginTop: 'initial',
                            marginBottom: 'initial',
                        }}
                    >
                        {dealsForUnitType?.length === 0 ? (
                            <option value=''>No offers available</option>
                        ) : (
                            <option value=''>Select an offer</option>
                        )}
                        {dealsForUnitType?.map((deal) => (
                            <option key={deal._id} value={deal._id}>
                                {`${deal.discountPlan?.sPlanName}`}
                            </option>
                        ))}
                    </select>
                </Card>

                <Card loading={isLoadingUnitTypes}>
                    <table className='w-full'>
                        <thead>
                            <tr>
                                <th></th>
                                <th
                                    className='p-2'
                                    style={{
                                        backgroundColor: configuration.secondaryColor,
                                        color: getTextColor(
                                            configuration.secondaryColor,
                                            '#fff'
                                        ),
                                    }}
                                >
                                    Offer Month
                                </th>
                                <th
                                    className='p-2'
                                    style={{
                                        backgroundColor: configuration.secondaryColor,
                                        color: getTextColor(
                                            configuration.secondaryColor,
                                            '#fff'
                                        ),
                                    }}
                                >
                                    STD Month
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td
                                    className='p-2'
                                    style={{
                                        backgroundColor: configuration.secondaryColor,
                                        color: getTextColor(
                                            configuration.secondaryColor,
                                            '#fff'
                                        ),
                                    }}
                                >
                                    Rent
                                </td>
                                <td className='p-2 bg-gray-400'>
                                    GBP{' '}
                                    {formatCurrency(
                                        selectedDealObject?.rates.discountedRate || 0
                                    )}
                                </td>
                                <td className='p-2 bg-gray-400'>
                                    GBP{' '}
                                    {formatCurrency(selectedUnitType?.rates.rate || 0)}
                                </td>
                            </tr>
                            <tr>
                                <td
                                    className='p-2'
                                    style={{
                                        backgroundColor: configuration.secondaryColor,
                                        color: getTextColor(
                                            configuration.secondaryColor,
                                            '#fff'
                                        ),
                                    }}
                                >
                                    StoreProtect
                                </td>
                                <td className='p-2 bg-pink-300'>
                                    GBP{' '}
                                    {formatCurrency(
                                        (selectedInsuranceObject?.dcPremium || 0) *
                                            INSURANCETAX
                                    )}
                                </td>
                                <td className='p-2 bg-pink-300'>
                                    GBP{' '}
                                    {formatCurrency(
                                        (selectedInsuranceObject?.dcPremium || 0) *
                                            INSURANCETAX
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td
                                    className='p-2'
                                    style={{
                                        backgroundColor: configuration.secondaryColor,
                                        color: getTextColor(
                                            configuration.secondaryColor,
                                            '#fff'
                                        ),
                                    }}
                                >
                                    Total
                                </td>
                                <td className='text-lg'>
                                    GBP{' '}
                                    {formatCurrency(
                                        (selectedDealObject?.rates.discountedRate || 0) +
                                            (selectedInsuranceObject?.dcPremium || 0) *
                                                INSURANCETAX
                                    )}
                                </td>
                                <td className='text-lg'>
                                    GBP{' '}
                                    {formatCurrency(
                                        (selectedUnitType?.rates.rate || 0) +
                                            (selectedInsuranceObject?.dcPremium || 0) *
                                                INSURANCETAX
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </Card>

                <div className='grid grid-cols-2 gap-4 items-start'>
                    <Card
                        loading={isLoadingUnitTypes}
                        className='flex flex-col items-center border-4 border-solid'
                    >
                        <span
                            className='py-4 text-lg text-center w-full'
                            style={{
                                backgroundColor: configuration.secondaryColor,
                                color: getTextColor(configuration.secondaryColor, '#fff'),
                            }}
                        >
                            Discount per week:{' '}
                        </span>
                        <span className='text-lg bg-red-400 text-black py-4 w-full text-center'>
                            GBP {formatCurrency(discountPerWeek, true)}
                        </span>
                    </Card>

                    <Card
                        loading={isLoadingUnitTypes}
                        className='flex flex-col items-center rounded border-4 border-solid'
                    >
                        <span
                            className='text-center text-lg py-4 w-full'
                            style={{
                                backgroundColor: configuration.secondaryColor,
                                color: getTextColor(configuration.secondaryColor, '#fff'),
                            }}
                        >
                            Discounted Weekly Price:{' '}
                        </span>
                        <span className='text-lg bg-red-400 text-black py-4 w-full text-center'>
                            GBP {formatCurrency(discountedWeeklyPrice, true)}
                        </span>
                    </Card>
                </div>

                <div className='grid grid-cols-2 gap-4 items-start'>
                    <Card
                        loading={isLoadingUnitTypes}
                        className='flex flex-col items-center rounded border-4 border-solid'
                    >
                        <span
                            className='text-center text-lg py-4 w-full'
                            style={{
                                backgroundColor: configuration.secondaryColor,
                                color: getTextColor(configuration.secondaryColor, '#fff'),
                            }}
                        >
                            £&apos;s reduction = % Reduced:{' '}
                        </span>
                        <span className='text-lg bg-red-400 text-black py-4 w-full text-center'>
                            {formatCurrency(percentageReduced)}%
                        </span>
                    </Card>

                    <Card
                        loading={isLoadingUnitTypes}
                        className='flex flex-col items-center rounded border-4 border-solid'
                    >
                        <span
                            className='text-center text-lg py-4 w-full'
                            style={{
                                backgroundColor: configuration.secondaryColor,
                                color: getTextColor(configuration.secondaryColor, '#fff'),
                            }}
                        >
                            Weekly Offer Price:{' '}
                        </span>
                        <span className='text-lg bg-red-400 text-black py-4 w-full text-center'>
                            GBP{' '}
                            {selectedDealObject
                                ? formatCurrency(weeklyOfferPrice, true)
                                : 0}
                        </span>
                    </Card>
                </div>

                {selectedUnitType ? (
                    <div>
                        <Button
                            onClick={() => setReservationModalOpen(true)}
                            disabled={!selectedUnitType || !selectedDealObject}
                            corporation={configuration}
                        >
                            Make Enquiry
                        </Button>
                    </div>
                ) : null}
            </div>

            <div className='flex flex-col gap-4'>
                <Card
                    loading={isLoadingUnitTypes}
                    className='flex items-center rounded border-4 border-solid'
                >
                    <span
                        className='block text-lg p-4 text-nowrap'
                        style={{
                            backgroundColor: configuration.secondaryColor,
                            color: getTextColor(configuration.secondaryColor, '#fff'),
                        }}
                    >
                        Unit Size:
                    </span>
                    <select
                        className='bg-gray-400 w-full p-4 text-lg border-none'
                        value={selectedUnitType?._id}
                        onChange={(e) => {
                            setSelectedUnitType(
                                unitTypes?.unitTypes.find(
                                    (u) => u._id === e.target.value
                                ) || null
                            );
                        }}
                        style={{
                            height: 'initial',
                            marginTop: 'initial',
                            marginBottom: 'initial',
                        }}
                    >
                        <option value=''>Select a unit size</option>
                        {sortedUnitTypes.map((unitType) => (
                            <option key={unitType._id} value={unitType._id}>
                                {unitType.area} {unitType.typeName}
                                {!isNaN(unitType.floor)
                                    ? ` - Floor: ${unitType.floor}`
                                    : ''}
                            </option>
                        ))}
                    </select>
                </Card>

                <Card
                    loading={isLoadingUnitTypes}
                    className='flex items-center border-4 border-solid'
                >
                    <span
                        className='p-4 text-lg text-nowrap'
                        style={{
                            backgroundColor: configuration.secondaryColor,
                            color: getTextColor(configuration.secondaryColor, '#fff'),
                        }}
                    >
                        STD Weekly Rate:{' '}
                    </span>
                    <span className='text-lg bg-gray-400 text-black p-4 flex-grow text-center text-nowrap'>
                        GBP {formatCurrency(selectedUnitType?.rates.rate || 0, true)}
                    </span>
                </Card>

                <Card
                    loading={isLoadingUnitTypes}
                    className='flex items-center border-4 border-solid'
                >
                    <span
                        className='p-4 text-lg text-nowrap'
                        style={{
                            backgroundColor: configuration.secondaryColor,
                            color: getTextColor(configuration.secondaryColor, '#fff'),
                        }}
                    >
                        Offer Weekly Rate:{' '}
                    </span>
                    <span className='text-lg bg-gray-400 text-black p-4 flex-grow text-center text-nowrap'>
                        GBP{' '}
                        {formatCurrency(
                            selectedDealObject?.rates.discountedRate || 0,
                            true
                        )}
                    </span>
                </Card>

                <Card
                    loading={isLoadingUnitTypes}
                    className='flex items-center border-4 border-solid'
                >
                    <span
                        className='p-4 text-lg'
                        style={{
                            backgroundColor: configuration.secondaryColor,
                            color: getTextColor(configuration.secondaryColor, '#fff'),
                        }}
                    >
                        StoreProtect:{' '}
                    </span>
                    <select
                        className='bg-pink-300 w-full border-2 p-4 border-black border-solid text-lg'
                        value={selectedInsurance}
                        onChange={(e) => {
                            setSelectedInsurance(e.target.value);
                        }}
                        style={{
                            height: 'initial',
                            marginTop: 'initial',
                            marginBottom: 'initial',
                        }}
                    >
                        <option value=''>Select an insurance</option>
                        {insuranceList?.results.map((coverage) => (
                            <option
                                key={coverage._id}
                                value={coverage._id}
                            >{`GBP ${coverage.dcCoverage}`}</option>
                        ))}
                    </select>
                </Card>

                <Card
                    loading={isLoadingUnitTypes}
                    className='flex items-center border-4 border-solid'
                >
                    <span
                        className='p-4 text-lg'
                        style={{
                            backgroundColor: configuration.secondaryColor,
                            color: getTextColor(configuration.secondaryColor, '#fff'),
                        }}
                    >
                        Weekly Price:{' '}
                    </span>
                    <span className='text-lg bg-pink-300 text-black p-4 flex-grow text-center'>
                        GBP{' '}
                        {formatCurrency(
                            (selectedInsuranceObject?.dcPremium || 0) * INSURANCETAX,
                            true
                        )}
                    </span>
                </Card>

                <div className='grid grid-cols-2 gap-4'>
                    <Card
                        loading={isLoadingUnitTypes}
                        className='flex flex-col items-center rounded border-4 border-solid'
                    >
                        <span
                            className='text-center text-lg py-4 w-full'
                            style={{
                                backgroundColor: configuration.secondaryColor,
                                color: getTextColor(configuration.secondaryColor, '#fff'),
                            }}
                        >
                            Offer Payment:{' '}
                        </span>
                        <span className='text-lg bg-gray-400 text-black py-4 w-full text-center'>
                            GBP {formatCurrency((weeklyOfferPrice * 52.143) / 12)}
                        </span>
                        <span className='text-lg bg-pink-300 text-black py-4 w-full text-center'>
                            GBP{' '}
                            {formatCurrency(
                                (selectedInsuranceObject?.dcPremium || 0) * INSURANCETAX
                            )}
                        </span>
                        <span className='text-lg bg-white text-black py-4 w-full text-center'>
                            GBP{' '}
                            {formatCurrency(
                                (weeklyOfferPrice * 52.143) / 12 +
                                    (selectedInsuranceObject?.dcPremium || 0) *
                                        INSURANCETAX
                            )}
                        </span>
                    </Card>

                    <Card
                        loading={isLoadingUnitTypes}
                        className='flex flex-col items-center rounded border-4 border-solid'
                    >
                        <span
                            className='text-center text-lg py-4 w-full'
                            style={{
                                backgroundColor: configuration.secondaryColor,
                                color: getTextColor(configuration.secondaryColor, '#fff'),
                            }}
                        >
                            Ongoing:{' '}
                        </span>
                        <span className='text-lg bg-gray-400 text-black py-4 w-full text-center'>
                            GBP {formatCurrency((discountedWeeklyPrice * 52.143) / 12)}
                        </span>
                        <span className='text-lg bg-pink-300 text-black py-4 w-full text-center'>
                            GBP{' '}
                            {formatCurrency(
                                (selectedInsuranceObject?.dcPremium || 0) * INSURANCETAX
                            )}
                        </span>
                        <span className='text-lg bg-white text-black py-4 w-full text-center'>
                            GBP{' '}
                            {formatCurrency(
                                (discountedWeeklyPrice * 52.143) / 12 +
                                    (selectedInsuranceObject?.dcPremium || 0) *
                                        INSURANCETAX
                            )}
                        </span>
                    </Card>
                </div>
            </div>

            <Modal show={reservationModalOpen}>
                <div className={`${reservationLoading ? 'opacity-50' : ''}`}>
                    <form className='flex flex-col gap-4' onSubmit={actionShowPrices}>
                        <div className='grid grid-cols-2 gap-4'>
                            <div className='flex flex-col'>
                                <label
                                    htmlFor='firstName'
                                    className='text-lg font-medium'
                                >
                                    First Name
                                </label>
                                <input
                                    required
                                    id='firstName'
                                    type='text'
                                    className='p-2 border border-gray-300 border-solid rounded-md'
                                    value={formData.firstName}
                                    onChange={(e) => {
                                        setFormData((draft) => {
                                            draft.firstName = e.target.value;
                                        });
                                    }}
                                />
                            </div>
                            <div className='flex flex-col'>
                                <label htmlFor='lastName' className='text-lg font-medium'>
                                    Last Name
                                </label>
                                <input
                                    required
                                    id='lastName'
                                    type='text'
                                    className='p-2 border border-gray-300 border-solid rounded-md'
                                    value={formData.lastName}
                                    onChange={(e) => {
                                        setFormData((draft) => {
                                            draft.lastName = e.target.value;
                                        });
                                    }}
                                />
                            </div>
                        </div>
                        <div className='grid grid-cols-2 gap-4'>
                            <div className='flex flex-col'>
                                <label htmlFor='email' className='text-lg font-medium'>
                                    Email
                                </label>
                                <input
                                    required
                                    id='email'
                                    type='email'
                                    className='p-2 border border-gray-300 border-solid rounded-md'
                                    value={formData.email}
                                    onChange={(e) => {
                                        setFormData((draft) => {
                                            draft.email = e.target.value;
                                        });
                                    }}
                                />
                            </div>
                            <div className='flex flex-col'>
                                <label htmlFor='phone' className='text-lg font-medium'>
                                    Phone
                                </label>
                                <input
                                    required
                                    id='phone'
                                    type='tel'
                                    className='p-2 border border-gray-300 border-solid rounded-md'
                                    value={formData.phone}
                                    onChange={(e) => {
                                        setFormData((draft) => {
                                            draft.phone = e.target.value;
                                        });
                                    }}
                                />
                            </div>
                        </div>
                        <div className='grid grid-cols-2 gap-4'>
                            <div className='flex flex-col'>
                                <label
                                    htmlFor='agentName'
                                    className='text-lg font-medium'
                                >
                                    {configuration.agentFieldType === 'email'
                                        ? 'Agent Email'
                                        : 'Agent Name'}
                                </label>
                                <input
                                    required
                                    id='agentName'
                                    type={
                                        configuration.agentFieldType === 'email'
                                            ? 'email'
                                            : 'text'
                                    }
                                    className='p-2 border border-gray-300 border-solid rounded-md'
                                    value={formData.agentName}
                                    onChange={(e) => {
                                        setFormData((draft) => {
                                            draft.agentName = e.target.value;
                                        });
                                    }}
                                />
                            </div>
                            <div className='flex flex-col'>
                                <label
                                    htmlFor='agentComment'
                                    className='text-lg font-medium'
                                >
                                    Agent Comment
                                </label>
                                <input
                                    required
                                    id='agentComment'
                                    type='text'
                                    className='p-2 border border-gray-300 border-solid rounded-md'
                                    value={formData.agentComment}
                                    onChange={(e) => {
                                        setFormData((draft) => {
                                            draft.agentComment = e.target.value;
                                        });
                                    }}
                                />
                            </div>
                        </div>
                        <div className='flex gap-4 items-center'>
                            <Button
                                disabled={reservationLoading}
                                type='button'
                                onClick={() => setReservationModalOpen(false)}
                                corporation={configuration}
                            >
                                Cancel
                            </Button>
                            <Button
                                disabled={reservationLoading}
                                type='submit'
                                corporation={configuration}
                            >
                                Make Enquiry
                            </Button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    );
}
